import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { PendingWork } from "../../types";
import { fetchPendingWork, adminGradeWork, chatPhotoUrl } from "../../utils/api";
import { AppHeader, Banner, Icon } from "../common/ui";
import Lightbox from "../common/Lightbox";

interface Props {
  token: string;
  onExit: () => void;
  theme: "dark" | "light";
  onThemeChange: (t: "dark" | "light") => void;
}

export default function AdminTaskReview({ token, onExit, theme, onThemeChange }: Props) {
  const { t, i18n } = useTranslation();
  const isRu = !i18n.language?.startsWith("en");

  const [items, setItems] = useState<PendingWork[] | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<string | null>(null);

  const load = useCallback(() => {
    fetchPendingWork(token).then(setItems).catch(() => setItems([]));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const grade = async (w: PendingWork, status: "passed" | "failed") => {
    setBusy(w.id); setError(null);
    try {
      await adminGradeWork(w.id, status, notes[w.id]?.trim() || undefined, token);
      setItems((prev) => (prev ? prev.filter((x) => x.id !== w.id) : prev));
      setNotes((prev) => { const n = { ...prev }; delete n[w.id]; return n; });
    } catch (e) {
      setError(e instanceof Error ? e.message : t("common.error"));
    } finally {
      setBusy(null);
    }
  };

  const fmt = (iso: string) =>
    new Date(iso).toLocaleString(isRu ? "ru-RU" : "en-US", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

  return (
    <div className="beeapp__scroll">
      {lightbox && <Lightbox src={lightbox} onClose={() => setLightbox(null)} />}
      <AppHeader theme={theme} onThemeChange={onThemeChange} onBack={onExit} title={t("review.title")} />

      <div className="wrap content-cap" style={{ paddingTop: 6, maxWidth: 640 }}>
        {error && <div style={{ marginBottom: 14 }}><Banner kind="error">{error}</Banner></div>}

        {items === null ? (
          <div style={{ display: "grid", placeItems: "center", padding: 40 }}>
            <div className="spinner spinner-amber" style={{ width: 28, height: 28, borderWidth: 3 }} />
          </div>
        ) : items.length === 0 ? (
          <div className="glass glass-lg" style={{ padding: 36, textAlign: "center" }}>
            <span style={{ display: "grid", placeItems: "center", width: 54, height: 54, borderRadius: 16, margin: "0 auto 12px", background: "var(--violet-tint)", color: "var(--violet-text)" }}>
              <Icon name="checkCircle" size={26} />
            </span>
            <p className="t-h" style={{ marginBottom: 4 }}>{t("review.emptyTitle")}</p>
            <p className="t-sm muted">{t("review.emptyHint")}</p>
          </div>
        ) : (
          <div className="stack" style={{ "--gap": "14px" } as React.CSSProperties}>
            {items.map((w) => {
              const task = isRu ? w.exercise_title_ru : (w.exercise_title_en || w.exercise_title_ru);
              const lesson = isRu ? w.lesson_title_ru : (w.lesson_title_en || w.lesson_title_ru);
              const photo = w.has_photo ? chatPhotoUrl(w.id, token) : null;
              const working = busy === w.id;
              return (
                <div key={w.id} className="glass glass-lg glass-sheen" style={{ padding: 16 }}>
                  {/* Who + when */}
                  <div className="row between" style={{ gap: 10, marginBottom: 10 }}>
                    <div className="row" style={{ gap: 10, minWidth: 0 }}>
                      <span style={{ display: "grid", placeItems: "center", width: 38, height: 38, borderRadius: 11, flex: "none", background: "var(--violet-tint)", color: "var(--violet-text)" }}>
                        <Icon name="user" size={18} />
                      </span>
                      <div style={{ minWidth: 0 }}>
                        <div className="t-h" style={{ lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{w.student_name}</div>
                        <div className="t-xs muted-2">{fmt(w.created_at)}</div>
                      </div>
                    </div>
                    <span className="badge badge-amber" style={{ flex: "none" }}>{t("review.pending")}</span>
                  </div>

                  {/* What task */}
                  {(task || lesson) && (
                    <div style={{ padding: "8px 12px", borderRadius: "var(--r-sm)", background: "var(--violet-tint)", marginBottom: 12 }}>
                      {lesson && <p className="eyebrow" style={{ marginBottom: 2, color: "var(--violet-text)" }}>{lesson}</p>}
                      {task && <p className="t-sm" style={{ margin: 0, fontWeight: 700 }}>{task}</p>}
                    </div>
                  )}

                  {/* Submitted text (if any) */}
                  {w.body && <p className="t-sm" style={{ fontWeight: 600, lineHeight: 1.5, marginBottom: 12, whiteSpace: "pre-wrap" }}>{w.body}</p>}

                  {/* Submitted photo */}
                  {photo && (
                    <img
                      src={photo}
                      alt="submission"
                      loading="lazy"
                      onClick={() => setLightbox(photo)}
                      style={{ width: "100%", maxHeight: 320, objectFit: "contain", display: "block", borderRadius: "var(--r-sm)", background: "var(--glass-1)", cursor: "zoom-in", marginBottom: 12 }}
                    />
                  )}

                  {/* Feedback field */}
                  <textarea
                    value={notes[w.id] ?? ""}
                    onChange={(e) => setNotes((prev) => ({ ...prev, [w.id]: e.target.value }))}
                    placeholder={t("review.feedbackPlaceholder")}
                    rows={2}
                    className="input"
                    style={{ width: "100%", resize: "vertical", minHeight: 44, marginBottom: 12, boxSizing: "border-box" }}
                  />

                  {/* Approve / send back */}
                  <div className="row" style={{ gap: 10 }}>
                    <button className="btn btn-glass" onClick={() => grade(w, "failed")} disabled={working}
                      style={{ flex: 1, color: "var(--error)" }}>
                      <Icon name="x" size={16} /> {t("review.decline")}
                    </button>
                    <button className="btn btn-primary" onClick={() => grade(w, "passed")} disabled={working}
                      style={{ flex: 1 }}>
                      {working ? <span className="spinner" /> : <><Icon name="check" size={16} /> {t("review.approve")}</>}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
